import { useTranslation } from 'react-i18next'
import { Col, Row, Toast, ToastContainer } from 'react-bootstrap'
import { useState } from 'react'
import PropTypes from 'prop-types'
import FullLogo from '../../assets/logos/full-logo.png'
import ProfileAvatar from '../profileAvatar'
import theme from '../../constants/theme'
import {
  ExternalLink,
  FooterContent,
  Typography,
  ScrollableContainer,
  InfoCard,
  HeroBannerSection,
  HeroBannerTitle,
  HeroBannerApplyButton,
  BannerDecorCircleLarge,
  BannerDecorCircleSmall,
  BannerTitleArea,
  BlockTypography,
  CardSectionHeader,
  CardIconBox,
  ProfileCardHeader,
  CompanyLogoImg,
  CompanyDivider,
  PrivacyRow,
  TalentPoolHeroBadge,
  TalentPoolHeroSubtitle,
  TalentPoolHeroActions,
  TalentPoolHeroNote,
  TalentPoolStatsGrid,
  TalentPoolStatCell,
  TalentPoolBenefitsGrid,
  TalentPoolBenefitItem,
  TalentPoolLinksRow,
  TalentPoolLinkChip,
  FieldMicroLabel,
} from '../styled'
import LanguagesDropdown from '../languageDropdown'
import PeopleFill from '../../assets/svgs/PeopleFill'
import ClockOutline from '../../assets/svgs/ClockOutline'
import GeoAlt from '../../assets/svgs/GeoAlt'
import House from '../../assets/svgs/House'
import Briefcase from '../../assets/svgs/Briefcase'
import Star from '../../assets/svgs/Star'
import UserOutline from '../../assets/svgs/UserOutline'
import LockClosed from '../../assets/svgs/LockClosed'
import ArrowRight from '../../assets/svgs/ArrowRight'
import {
  formatCountryName,
  formatWebsiteUrl,
} from '../../utilities/stringUtilities'

function TalentPoolInfo({ talentPool, company, onApplyClick }) {
  const { t } = useTranslation()
  const [showToast, setShowToast] = useState(false)

  const handleExternalLinkClick = (url, _linkType) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const companyStats = [
    company?.workingLocations?.length > 0 && {
      id: 'location',
      label: t('general.location'),
      value: company.workingLocations.map(formatCountryName).join(', '),
      Icon: GeoAlt,
    },
    company?.industry && {
      id: 'industry',
      label: t('card.industry'),
      value: company.industry,
      Icon: House,
    },
    company?.companySize && {
      id: 'companySize',
      label: t('card.companySize'),
      value: company.companySize,
      Icon: PeopleFill,
    },
    company?.openPositions && {
      id: 'openPositions',
      label: t('pages.openPositions'),
      value: t('pages.openPositionsCount', { count: company.openPositions }),
      Icon: Briefcase,
    },
  ].filter(Boolean)

  const joiningBenefits = [
    {
      id: 'matched',
      title: t('pages.matchedBeforePublicTitle'),
      description: t('pages.matchedBeforePublicDesc'),
      Icon: Star,
    },
    {
      id: 'oneProfile',
      title: t('pages.oneProfileManyRolesTitle'),
      description: t('pages.oneProfileManyRolesDesc'),
      Icon: UserOutline,
    },
  ]

  return (
    <div style={{ backgroundColor: '#F1F5FA', overflowX: 'clip' }}>
      <Row
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: theme.white,
          padding: '0px 20px',
        }}
      >
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />
        <Col xs="auto" className="mt-3">
          <LanguagesDropdown />
        </Col>
      </Row>

      <ScrollableContainer className="mt-4 fw-light">
        <Row className="mx-0 mb-3">
          <Col className="px-0">
            <HeroBannerSection>
              <BannerDecorCircleLarge />
              <BannerDecorCircleSmall />
              <BannerTitleArea>
                <TalentPoolHeroBadge>
                  <PeopleFill
                    width="12"
                    height="12"
                    color="rgba(255,255,255,0.92)"
                  />
                  {t('pages.talentPoolInvitationBadge')}
                </TalentPoolHeroBadge>

                <HeroBannerTitle>
                  {t('pages.talentPoolInvitation', {
                    company: company?.companyName,
                    talentPool: talentPool?.name,
                  })}
                </HeroBannerTitle>

                <TalentPoolHeroSubtitle>
                  {talentPool?.description ||
                    t('pages.talentPoolHeroSubtitle', {
                      company: company?.companyName,
                    })}
                </TalentPoolHeroSubtitle>

                <TalentPoolHeroActions>
                  <HeroBannerApplyButton onClick={onApplyClick}>
                    <PeopleFill
                      width="15"
                      height="15"
                      color={theme.talentinoSpecialBlue}
                    />
                    {t('pages.joinTalentPool')}
                  </HeroBannerApplyButton>
                  <TalentPoolHeroNote>
                    <ClockOutline
                      width="13"
                      height="13"
                      color="rgba(255,255,255,0.75)"
                    />
                    {t('pages.takesLessAMinute')}
                  </TalentPoolHeroNote>
                </TalentPoolHeroActions>
              </BannerTitleArea>
            </HeroBannerSection>
          </Col>
        </Row>

        <Row className="mx-0 mb-3">
          <InfoCard className="px-4 py-3">
            <ProfileCardHeader className="flex-wrap">
              {company?.logo ? (
                <CompanyLogoImg
                  src={company.logo}
                  alt={`${company?.companyName} logo`}
                />
              ) : (
                <ProfileAvatar name={company?.companyName} />
              )}
              <div className="flex-grow-1">
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <BlockTypography
                    bold
                    size="16px"
                    color={theme.talentinoBlack}
                  >
                    {company?.companyName}
                  </BlockTypography>
                </div>
                <Typography
                  size="12px"
                  regular
                  color={theme.talentinoSecondaryGray}
                >
                  {t('pages.hiringThroughTalentino')}
                </Typography>
              </div>
              {company?.website && (
                <ExternalLink
                  href={formatWebsiteUrl(company.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  color={theme.talentinoBlue}
                  className="d-inline-flex align-items-center gap-1 fw-semibold"
                  style={{ fontSize: '13px' }}
                >
                  {company.website}
                  <ArrowRight width="12" height="12" />
                </ExternalLink>
              )}
            </ProfileCardHeader>

            {companyStats.length > 0 && (
              <>
                <CompanyDivider className="mt-3" />
                <TalentPoolStatsGrid className="py-2">
                  {companyStats.map(({ id, label, value, Icon }) => (
                    <TalentPoolStatCell key={id}>
                      <CardIconBox>
                        <Icon
                          width="14"
                          height="14"
                          color={theme.talentinoSpecialBlue}
                        />
                      </CardIconBox>
                      <div>
                        <FieldMicroLabel>{label}</FieldMicroLabel>
                        <Typography
                          size="13px"
                          color={theme.talentinoBlack}
                          className="d-block lh-sm"
                        >
                          {value}
                        </Typography>
                      </div>
                    </TalentPoolStatCell>
                  ))}
                </TalentPoolStatsGrid>
              </>
            )}
          </InfoCard>
        </Row>

        <Row className="mx-0 mb-3">
          <InfoCard className="px-4 py-3">
            <CardSectionHeader mb="14px">
              <BlockTypography bold size="15px" color={theme.talentinoBlack}>
                {t('pages.whatJoiningMeans')}
              </BlockTypography>
            </CardSectionHeader>
            <TalentPoolBenefitsGrid>
              {joiningBenefits.map(({ id, title, description, Icon }) => (
                <TalentPoolBenefitItem key={id}>
                  <CardIconBox>
                    <Icon
                      width="14"
                      height="14"
                      color={theme.talentinoSpecialBlue}
                    />
                  </CardIconBox>
                  <div>
                    <BlockTypography
                      bold
                      size="13px"
                      color={theme.talentinoBlack}
                    >
                      {title}
                    </BlockTypography>
                    <Typography
                      size="12px"
                      regular
                      color={theme.talentinoSecondaryGray}
                      className="d-block lh-sm mt-1"
                    >
                      {description}
                    </Typography>
                  </div>
                </TalentPoolBenefitItem>
              ))}
            </TalentPoolBenefitsGrid>
          </InfoCard>
        </Row>

        {(talentPool?.careerPageUrl ||
          talentPool?.resourceLinks?.length > 0) && (
          <Row className="mx-0 mb-3">
            <InfoCard className="px-4 py-3">
              <CardSectionHeader mb="12px">
                <BlockTypography bold size="15px" color={theme.talentinoBlack}>
                  {t('pages.externalResources')}
                </BlockTypography>
              </CardSectionHeader>
              <TalentPoolLinksRow>
                {talentPool.careerPageUrl && (
                  <TalentPoolLinkChip
                    type="button"
                    onClick={() =>
                      handleExternalLinkClick(
                        talentPool.careerPageUrl,
                        'career-page',
                      )
                    }
                  >
                    {t('pages.careerPage')}
                    <ArrowRight width="11" height="11" />
                  </TalentPoolLinkChip>
                )}
                {talentPool.resourceLinks?.map((link, index) => (
                  <TalentPoolLinkChip
                    type="button"
                    key={link.url || index}
                    onClick={() =>
                      handleExternalLinkClick(link.url, `resource-${index}`)
                    }
                  >
                    {link.title}
                    <ArrowRight width="11" height="11" />
                  </TalentPoolLinkChip>
                ))}
              </TalentPoolLinksRow>
            </InfoCard>
          </Row>
        )}

        <PrivacyRow className="justify-content-center mb-4">
          <LockClosed width="12" height="12" />
          <Typography size="12px" regular color={theme.talentinoSecondaryGray}>
            {t('pages.talentPoolPrivacyNote', {
              company: company?.companyName,
            })}
          </Typography>
        </PrivacyRow>
      </ScrollableContainer>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body>{t('pages.talentPoolLinkCopied')}</Toast.Body>
        </Toast>
      </ToastContainer>

      <FooterContent className="d-flex py-2 border-t border-gray-200">
        {t('footer.TalentinoAI')} © {new Date().getFullYear()}{' '}
        {t('footer.allRightsReserved')}
      </FooterContent>
    </div>
  )
}

TalentPoolInfo.propTypes = {
  talentPool: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    careerPageUrl: PropTypes.string,
    resourceLinks: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  company: PropTypes.shape({
    companyName: PropTypes.string,
    logo: PropTypes.string,
    website: PropTypes.string,
    industry: PropTypes.string,
    companySize: PropTypes.string,
    openPositions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workingLocations: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onApplyClick: PropTypes.func.isRequired,
}

export default TalentPoolInfo
